import React, { useState } from 'react';
import { Landmark } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { ColorPicker } from '@components/ColorPicker';
import { Switch } from '@components/Switch';
import accountService from '@services/accountService';
import { ACCOUNT_TYPES, ACCOUNT_TYPE_LABELS, type Account, type AccountInput, type AccountType } from '@/types/finance';
import { DEFAULT_COLOR } from '@/utils/colors';
import { Required, labelCls } from './financeBits';

interface Props {
  account?: Account | null;
  onClose: () => void;
  onSaved: (account: Account) => void;
  onError: (err: unknown) => void;
}

interface FormState {
  name: string;
  account_type: AccountType;
  bank_code: string;
  bank_name: string;
  branch: string;
  number: string;
  color: string;
  is_active: boolean;
}

/** Cadastro/edição de conta (POST/PUT /accounts). */
export const AccountFormModal: React.FC<Props> = ({ account, onClose, onSaved, onError }) => {
  const isEdit = !!account;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(() => ({
    name: account?.name ?? '',
    account_type: account?.account_type ?? 'checking',
    bank_code: account?.bank_code ?? '',
    bank_name: account?.bank_name ?? '',
    branch: account?.branch ?? '',
    number: account?.number ?? '',
    color: account?.color ?? DEFAULT_COLOR,
    is_active: account?.is_active ?? true,
  }));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const canSubmit = !saving && !!form.name.trim();

  const save = async () => {
    if (!canSubmit) return;
    const payload: AccountInput = {
      name: form.name.trim(),
      account_type: form.account_type,
      bank_code: form.bank_code.trim() || null,
      bank_name: form.bank_name.trim() || null,
      branch: form.branch.trim() || null,
      number: form.number.trim() || null,
      color: form.color,
      is_active: form.is_active,
    };
    setSaving(true);
    try {
      onSaved(account ? await accountService.update(account.id, payload) : await accountService.create(payload));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Landmark size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar conta' : 'Nova conta'}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className={labelCls}>
            Nome
            <Required />
          </label>
          <input className="input-field" maxLength={100} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex.: Itaú corrente" autoFocus />
        </div>
        <div>
          <label className={labelCls}>
            Tipo
            <Required />
          </label>
          <select className="select-field" value={form.account_type} onChange={(e) => set('account_type', e.target.value as AccountType)}>
            {ACCOUNT_TYPES.map((t) => (
              <option key={t} value={t}>
                {ACCOUNT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className={labelCls}>Banco (cód.)</label>
            <input className="input-field" maxLength={10} value={form.bank_code} onChange={(e) => set('bank_code', e.target.value)} placeholder="341" />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Banco</label>
            <input className="input-field" maxLength={100} value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} placeholder="Itaú" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Agência</label>
          <input className="input-field" maxLength={20} value={form.branch} onChange={(e) => set('branch', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Número</label>
          <input className="input-field" maxLength={30} value={form.number} onChange={(e) => set('number', e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Cor</label>
          <ColorPicker value={form.color} onChange={(c) => set('color', c)} />
        </div>
        {isEdit && (
          <div className="sm:col-span-2">
            <Switch checked={form.is_active} onChange={(v) => set('is_active', v)} label="Conta ativa" />
            <p className="text-small mt-1">Conta inativa some das listas e dos seletores, mas mantém o histórico de lançamentos.</p>
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : 'Cadastrar'}
        </Button>
      </div>
    </Modal>
  );
};
